import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const langCode = i18n.language || 'en';

  const scrollToSection = (hash: string) => {
    const id = hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNavigate = (path: string) => {
    const isAnchor = path.includes('#');
    if (!isAnchor) {
      navigate(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const hash = path.split('#')[1] || '';
    const basePath = path.split('#')[0];
    const homePaths = ['/', `/${langCode}`, `/${langCode}/`];
    const alreadyHome = homePaths.includes(location.pathname);

    if (alreadyHome) {
      scrollToSection(hash);
    } else {
      navigate(`/${langCode}/`);
      setTimeout(() => scrollToSection(hash), 400);
    }
  };

  const footerLinks = [
    {
      title: 'CSSBuy Spreadsheet',
      links: [
        { label: t('footer.nav.spreadsheet'), href: `/${langCode}/` },
        { label: t('footer.nav.guide'), href: `/${langCode}/#guides` },
        { label: t('footer.nav.list'), href: `/${langCode}/#categories` },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: t('footer.nav.safety'), href: `/${langCode}/blog/cssbuy-safety` },
        { label: t('footer.nav.faq'), href: `/${langCode}/#faq` },
        { label: t('footer.nav.blog'), href: `/${langCode}/blog/cssbuy-spreadsheet-guide` },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: t('footer.nav.about'), href: `/${langCode}/about/` },
        { label: t('footer.nav.privacy'), href: `/${langCode}/privacy/` },
        { label: t('footer.nav.terms'), href: `/${langCode}/terms/` },
        { label: t('footer.nav.disclaimer'), href: `/${langCode}/disclaimer/` },
      ],
    },
  ];

  return (
    <footer className="bg-background-100 border-t border-background-200">
      <div className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <Link to={`/${langCode}/`} className="inline-block">
              <span className="font-heading text-xl font-semibold text-foreground-950">
                CSSBuy<span className="text-primary-600"> Spreadsheet</span>
              </span>
            </Link>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              {t('general.independentNotice')}
            </p>
          </div>

          {/* Link columns */}
          {footerLinks.map((column) => (
            <div key={column.title}>
              <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4">
                {column.title}
              </h4>
              <ul className="space-y-2.5">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      onClick={(e) => {
                        if (link.href.includes('#')) {
                          e.preventDefault();
                          handleNavigate(link.href);
                        }
                      }}
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-12 pt-8 border-t border-background-200">
          <p className="text-xs text-foreground-500 text-center leading-relaxed max-w-3xl mx-auto">
            {t('footer.disclaimer')}
          </p>
          <p className="text-xs text-foreground-400 text-center mt-4">
            {new Date().getFullYear()} CSSBuy Spreadsheet. {t('general.independentNotice')}
          </p>
        </div>
      </div>
    </footer>
  );
}